import { Button } from '@studio/design-system/button';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { ReactElement } from 'react';
import { Sel, STagNew } from '#~/stag/tag.js';
import { FunnelShell } from '../components/funnel-shell.js';
import { useAutoFocus } from '../hooks/use-auto-focus.js';
import { useStepView } from '../hooks/use-step-view.js';

interface ScreenCompleteProps {
  readonly cameraName: string;
  readonly onDone: () => void;
}

// The end of the funnel (ENG-3796 spec §5 / prototype "Complete").
//
// Nothing is recorded here. By the time this renders the camera is already
// added, which makes the user `activated` server-side, and `onboarded` is
// computed as (onboarding_completed_at != NULL) OR (an activation exists) — so
// it is already true and permanent. This screen is a confirmation, not a write,
// and adding one would be a second source of truth for a question the database
// has already answered.
//
// Per rules: code-comments-required, reference-rules-in-comments,
// ui-architecture (ai.knowledge/global).
export const ScreenComplete = ({ cameraName, onDone }: ScreenCompleteProps): ReactElement => {
  useStepView('complete');

  const primaryRef = useAutoFocus<HTMLButtonElement>();

  return (
    <FunnelShell announceTitle="You're all set!" onEscape={onDone} progressStep={5}>
      <div
        aria-hidden='true'
        className='w-56 bg-success/20 flex h-56 items-center justify-center rounded-full'
        style={{ boxShadow: '0 0 40px var(--ds-success)' }}
      >
        <svg className='h-28 w-28 text-success' fill='none' viewBox='0 0 24 24'>
          <path
            d='M5 12l4 4 10-10'
            stroke='currentColor'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2.4'
          />
        </svg>
      </div>

      <h1 className='mt-20 text-2xl font-demibold text-white'>You&apos;re all set!</h1>
      <p className='mt-8 max-w-[24rem] text-md text-muted'>
        {cameraName || 'Your camera'} is connected and ready to use.
      </p>

      {/* ONE way out, on purpose.
          The prototype offers "Add another camera" here, and it is deliberately
          not shown yet: re-entering the funnel exercises the add path a second
          time, and that path still has open defects (Re-test is blocked by the
          plan limit once a device is in the room, and add errors are computed
          but never rendered). Offering the loop before those are fixed sends
          users into a state we already know is broken. */}
      <div className='mt-28 flex flex-col items-center gap-12'>
        {/* vendorName(), never a literal: this ships whitelabelled, and a
            hardcoded product name is wrong in every variant but one. */}
        <Button
          {...STagNew(Sel.OnboardingComplete, 'done')}
          onClick={onDone}
          ref={primaryRef}
          size='lg'
          variant='primary'
        >
          Start using {vendorName()}
        </Button>
      </div>
    </FunnelShell>
  );
};
